import { useMemo, useState } from 'react';
import { CheckCircle2, Filter, Inbox as InboxIcon, Search, Truck, XCircle } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { Badge, Button, Card, CardBody, EmptyState, Select, useToast } from '@solara/ui-shared';
import type { InboxItemDto } from '@solara/api-client';
import { useInbox, useMarkInboxRead } from '@solara/api-client/react';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type InboundState = 'received' | 'reviewed' | 'paid' | 'disputed';
interface InboundRow extends InboxItemDto {
  invoiceNumber: string;
  senderName: string;
  senderNameAr: string;
  senderTrn: string;
  totalGrossAmount: number;
  attachmentCount: number;
  state: InboundState;
  erpWriteback: 'pending' | 'success' | 'failed';
}

function adapt(item: InboxItemDto): InboundRow {
  // Backend joins the invoice; surface its totals + buyer/seller for the row.
  const raw = item as InboxItemDto & {
    invoice?: {
      invoiceNumber?: string;
      sellerName?: string;
      sellerTrn?: string;
      buyerName?: string;
      totalMinor?: string;
    };
    flag?: 'NEEDS_REVIEW' | 'AUTO_CATEGORISED' | 'DUPLICATE_SUSPECT' | 'PROCESSED';
  };
  const inv = raw.invoice;
  const total = inv?.totalMinor ? Number(inv.totalMinor) / 100 : 0;
  const flag = raw.flag ?? (item.readAt ? 'PROCESSED' : 'NEEDS_REVIEW');
  const state: InboundState =
    flag === 'PROCESSED' ? 'reviewed' : 'received';
  return {
    ...item,
    invoiceNumber: inv?.invoiceNumber ?? item.subject ?? item.id.slice(0, 8),
    senderName: inv?.sellerName ?? item.preview ?? item.subject ?? '—',
    senderNameAr: inv?.sellerName ?? item.preview ?? item.subject ?? '—',
    senderTrn: inv?.sellerTrn ?? '',
    totalGrossAmount: total,
    attachmentCount: 0,
    state,
    erpWriteback: 'pending',
  };
}

type StateFilter = 'all' | 'received' | 'reviewed' | 'paid' | 'disputed';

export default function InboxPage() {
  const { t } = useTranslation('inbox');
  const { t: tTs } = useTranslation('tickstar');
  const { locale } = useI18n();
  const { toast } = useToast();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');

  const inboxQuery = useInbox({ search: query || undefined });
  const markRead = useMarkInboxRead();
  const items = useMemo(
    () => (inboxQuery.data ?? []).map(adapt),
    [inboxQuery.data],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((inv) => {
      if (stateFilter !== 'all' && inv.state !== stateFilter) return false;
      if (!q) return true;
      return (
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.senderName.toLowerCase().includes(q)
      );
    });
  }, [items, query, stateFilter]);

  const counts = useMemo(() => {
    const c = { received: 0, reviewed: 0, paid: 0, disputed: 0, erpFailed: 0 };
    for (const inv of items) c[inv.state]++;
    return c;
  }, [items]);

  function transition(inv: InboundRow, next: InboundState) {
    if (next === 'reviewed') {
      markRead.mutate(inv.id);
    }
    toast({
      title: t('transitionToast.title', { number: inv.invoiceNumber, state: t(`state.${next}`) }),
      description: t('transitionToast.body'),
      tone: 'info',
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">{tTs('modules.smartInbox.title')}</h1>
            <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium">
              {tTs('modules.smartInbox.aiBadge')}
            </span>
          </div>
          <p className="text-fg-muted text-sm">{tTs('modules.smartInbox.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
            <input
              type="search"
              placeholder={t('search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full sm:w-64 h-11 ps-9 pe-3 rounded-lg bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
            />
          </div>
          <Select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            options={[
              { value: 'all', label: t('filter.all') },
              { value: 'received', label: t('filter.received') },
              { value: 'reviewed', label: t('filter.reviewed') },
              { value: 'paid', label: t('filter.paid') },
              { value: 'disputed', label: t('filter.disputed') },
            ]}
            containerClassName="w-44"
          />
          <Button variant="outline" size="md" className="gap-2">
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">{t('more')}</span>
          </Button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-4">
        <KpiCard label={t('kpi.received')} value={counts.received} icon={<InboxIcon className="h-4 w-4" />} />
        <KpiCard label={t('kpi.reviewed')} value={counts.reviewed} icon={<CheckCircle2 className="h-4 w-4" />} />
        <KpiCard label={t('kpi.paid')} value={counts.paid} icon={<Truck className="h-4 w-4" />} />
        <KpiCard label={t('kpi.disputed')} value={counts.disputed} icon={<XCircle className="h-4 w-4" />} tone="danger" />
      </section>

      {inboxQuery.isLoading ? (
        <LoadingBlock />
      ) : inboxQuery.error ? (
        <ErrorBanner error={inboxQuery.error} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<InboxIcon className="h-6 w-6" />} title={t('empty')} />
      ) : (
        <div className="grid gap-3">
          {filtered.map((inv) => (
            <Card key={inv.id} className="rounded-2xl">
              <CardBody className="p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{inv.invoiceNumber}</span>
                    <StateBadge state={inv.state} t={t} />
                    <ErpBadge erp={inv.erpWriteback} t={t} />
                  </div>
                  <div className="text-sm text-fg-muted truncate" dir="auto">
                    {locale === 'ar-AE' ? inv.senderNameAr : inv.senderName}
                  </div>
                  <div className="text-xs text-fg-subtle">
                    {t('row.received', { when: dateFmt.format(new Date(inv.receivedAt)) })} ·{' '}
                    {t('row.trn', { trn: inv.senderTrn })}
                    {inv.attachmentCount > 0 ? <> · {t('row.attachments', { count: inv.attachmentCount })}</> : null}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap lg:flex-nowrap shrink-0">
                  <div className="text-end">
                    <div className="text-lg font-semibold num">{aedFmt.format(inv.totalGrossAmount)}</div>
                    <div className="text-xs text-fg-subtle">{t('row.inclVat')}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {inv.state === 'received' ? (
                      <Button size="sm" onClick={() => transition(inv, 'reviewed')}>{t('actions.markReviewed')}</Button>
                    ) : null}
                    {inv.state === 'reviewed' ? (
                      <Button size="sm" onClick={() => transition(inv, 'paid')}>{t('actions.markPaid')}</Button>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => transition(inv, 'disputed')}>
                      {t('actions.dispute')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StateBadge({ state, t }: { state: InboundState; t: (k: string) => string }) {
  const tone = state === 'paid' ? 'success' : state === 'reviewed' ? 'brand' : state === 'disputed' ? 'danger' : 'info';
  return <Badge tone={tone}>{t(`state.${state}`)}</Badge>;
}

function ErpBadge({ erp, t }: { erp: InboundRow['erpWriteback']; t: (k: string) => string }) {
  const tone = erp === 'success' ? 'success' : erp === 'pending' ? 'warning' : 'danger';
  return <Badge tone={tone}>{t(`erp.${erp}`)}</Badge>;
}

function KpiCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: 'danger';
}) {
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 flex items-center gap-3">
        <div
          className={
            tone === 'danger'
              ? 'h-10 w-10 grid place-items-center rounded-xl bg-red-50 text-danger shrink-0'
              : 'h-10 w-10 grid place-items-center rounded-xl bg-brand-50 text-brand-700 shrink-0'
          }
        >
          {icon}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
          <div className="text-xl font-semibold num">{value}</div>
        </div>
      </CardBody>
    </Card>
  );
}
